import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getProductListByColumn } from '../../../api/services/productService';
import ProductCard from '../../../components/ProductCard';

const SectionContainer = styled.section`
  width: 100%;
  padding: 0 1.5%;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  margin-top: 50px;
  text-align: left;
  color: ${(props) => props.theme.colors.primaryText};
  width: 90%;

  /* Tablet responsive */
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
`;

const ProductCardWrapper = styled.div`
  width: 90%;
  > div {
    width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const GridContainer = styled.div`
  display: grid;
  max-width: 90%;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 20px;
  gap: 10px;
  width: 100%;

  /* Tablet responsive */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    display: none;
  }
`;

// Mobile Slider Container
const MobileSlider = styled.div`
  display: none;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-top: 20px;

  @media (max-width: 480px) {
    display: block;
    width: 100%;
    padding: 0 5px 40px 5px;
  }
`;

const SliderTrack = styled.div`
  display: flex;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${(props) => props.currentSlide * -100}%);
`;

const DotsContainer = styled.div`
  display: none;
  position: absolute;
  width: 100%;
  bottom: 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;

  @media (max-width: 480px) {
    display: flex;
    bottom: 15px;
    gap: 6px;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${(props) =>
    props.active &&
    `
      background-color: #000;
      transform: scale(1.2);
    `}

  @media (max-width: 480px) {
    width: 6px;
    height: 6px;
  }
`;

export default function BestSellersView() {
  const userInfo = useSelector((state) => state.userInfo);
  const [bestSellersProductList, setBestSellersProductList] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    getBestSellersProductList();
  }, []);

  const getBestSellersProductList = async () => {
    try {
      const bestSellersResult = await getProductListByColumn(
        'bestSeller',
        userInfo.idCustomer,
        userInfo.brandId,
        '01', // Always use price level 01
      );

      // Take first 12 products for best sellers display (3x4 grid)
      const bestSellersProducts =
        bestSellersResult.productList?.slice(0, 12) || [];

      // Filter out products without valid images
      const productsWithValidImages = bestSellersProducts.filter((product) => {
        const imagePath = product.image || product.filepath1;
        return imagePath && imagePath.trim() !== '';
      });

      setBestSellersProductList(productsWithValidImages);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === Math.ceil(bestSellersProductList.length / 2) - 1
        ? prev
        : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? prev : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <SectionContainer>
      {bestSellersProductList.length > 0 ? (
        <>
          <SectionTitle>Best Sellers</SectionTitle>

          {/* Desktop/Tablet Grid */}
          <GridContainer>
            {bestSellersProductList.map((item, index) => (
              <ProductCardWrapper key={`product_${index}`}>
                <ProductCard data={item} key={`product_${index}`} />
              </ProductCardWrapper>
            ))}
          </GridContainer>

          {/* Mobile Slider */}
          <MobileSlider
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <SliderTrack currentSlide={currentSlide}>
              {bestSellersProductList.map((item, index) => (
                <div
                  key={`product_${index}`}
                  style={{
                    width: '50%',
                    flexShrink: 0,
                    padding: '0 5px',
                  }}
                >
                  <ProductCard data={item} key={`product_${index}`} />
                </div>
              ))}
            </SliderTrack>
            <DotsContainer>
              {Array.from({
                length: Math.ceil(bestSellersProductList.length / 2),
              }).map((_, idx) => (
                <Dot
                  key={idx}
                  active={idx === currentSlide}
                  onClick={() => goToSlide(idx)}
                />
              ))}
            </DotsContainer>
          </MobileSlider>
        </>
      ) : null}
    </SectionContainer>
  );
}
